import React, { useEffect } from "react";
import PropTypes from "prop-types";
import useAxios from "axios-hooks";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";

const DeleteModal = ({
  showModal,
  toggleShowModal,
  onDelete,
  setErrorMsg,
  setShowError,
  setShowSuccess,
  deleteUrl,
  subText,
  deleteButtonText = "Delete",
  cancelButtonText = "Cancel",
  errorMessage = "An error occurred. Please refresh the page and try again.",
  resourceName,
  resourceType,
  confirmationValue,
  confirmLabel,
  onDeleteResult,
}) => {
  const [{ data, loading, error }, executeDelete] = useAxios(
    {
      method: "delete",
      url: deleteUrl,
    },
    { manual: true }
  );

  // handleDelete always closes the modal, so only the caller notification belongs here.
  useEffect(() => {
    if (!loading && data && !error) {
      onDelete && onDelete();
    }
  }, [loading, data, error, onDelete]);

  const handleDelete = async () => {
    try {
      const result = await executeDelete();
      if (!result.isAxiosError) {
        // A caller returning true has shown its own message, so skip the success toast and reload.
        const handled = onDeleteResult ? onDeleteResult(result.data) : false;
        if (!handled) {
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess?.(false);
            window.location.reload();
          }, 2000);
        }
      } else {
        setShowError(true);
        setErrorMsg(errorMessage);
      }
    } catch (error) {
      setShowError(true);
      setErrorMsg(errorMessage);
    } finally {
      // Regardless of outcome we want to hide the modal so the user
      // can see their error/success message
      toggleShowModal();
    }
  };

  return (
    <DeleteConfirmationDialog
      isVisible={showModal}
      onDismiss={toggleShowModal}
      onConfirm={handleDelete}
      message={subText}
      resourceName={resourceName}
      resourceType={resourceType}
      confirmationValue={confirmationValue}
      confirmLabel={confirmLabel}
      deleteButtonText={deleteButtonText}
      cancelButtonText={cancelButtonText}
      isDeleting={loading}
    />
  );
};

export default DeleteModal;

DeleteModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  toggleShowModal: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  setErrorMsg: PropTypes.func.isRequired,
  setShowError: PropTypes.func.isRequired,
  setShowSuccess: PropTypes.func.isRequired,
  deleteUrl: PropTypes.string.isRequired,
  subText: PropTypes.string,
  deleteButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  errorMessage: PropTypes.string,
  resourceName: PropTypes.string,
  resourceType: PropTypes.string,
  confirmationValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  confirmLabel: PropTypes.string,
  onDeleteResult: PropTypes.func,
};